import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Loader2, Eye, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import MediaUploader from "../../components/admin/MediaUploader";
import SeoTab from "../../components/seo/SeoTab";
import api, { formatApiError } from "../../lib/api";

const EMPTY = {
  name: "", slug: "", short_description: "", description: "", sort_order: 0,
  is_active: true, is_featured: false, image: "", meta_title: "", meta_description: "",
  seo: { auto: true },
};

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/categories").then(({ data }) => setCats(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({ ...EMPTY, ...c });
    setEditingId(c.category_id);
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editingId) await api.patch(`/admin/categories/${editingId}`, form);
      else await api.post("/admin/categories", form);
      toast.success(editingId ? "Category updated" : "Category created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const toggle = async (c) => {
    try {
      await api.post(`/admin/categories/${c.category_id}/toggle`);
      load();
    } catch (err) { toast.error("Could not update status"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/categories/${c.category_id}`);
      toast.success("Category deleted");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not delete category"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-categories">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl text-white">Categories</h1>
          <p className="mt-2 text-muted-foreground">Organise the catalogue into browsable categories.</p>
        </div>
        <Button data-testid="add-category-btn" onClick={openNew} className="cf-gold-btn font-semibold gap-2"><Plus className="h-4 w-4" /> Add Category</Button>
      </div>

      <GlassCard className="p-0 overflow-hidden" hover={false}>
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>
        ) : cats.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">No categories yet. Create your first one.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/70">Category</TableHead>
                <TableHead className="text-white/70">Slug</TableHead>
                <TableHead className="text-white/70">Products</TableHead>
                <TableHead className="text-white/70">Featured</TableHead>
                <TableHead className="text-white/70">Status</TableHead>
                <TableHead className="text-white/70 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cats.map((c) => (
                <TableRow key={c.category_id} className="border-white/10" data-testid={`category-row-${c.slug}`}>
                  <TableCell className="text-white font-medium flex items-center gap-3">
                    {c.image && <img src={c.image} alt="" className="h-8 w-8 rounded object-cover" />}
                    {c.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{c.slug}</TableCell>
                  <TableCell className="text-muted-foreground">{c.product_count ?? 0}</TableCell>
                  <TableCell>{c.is_featured ? <Star className="h-4 w-4 text-gold fill-gold" /> : <span className="text-white/30">—</span>}</TableCell>
                  <TableCell>
                    <button data-testid={`toggle-category-${c.slug}`} onClick={() => toggle(c)} className={c.is_active ? "text-emerald-400 text-sm" : "text-firered text-sm"}>
                      {c.is_active ? "Active" : "Inactive"}
                    </button>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <a href={`/fireworks/${c.slug}`} target="_blank" rel="noreferrer" title="Preview" className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-gold hover:bg-white/10" data-testid={`preview-category-${c.slug}`}><Eye className="h-4 w-4" /></a>
                    <button onClick={() => openEdit(c)} title="Edit" data-testid={`edit-category-${c.slug}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-gold hover:bg-white/10"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(c)} title="Delete" data-testid={`delete-category-${c.slug}`} className="inline-grid place-items-center h-8 w-8 rounded-md text-white/60 hover:text-firered hover:bg-firered/10"><Trash2 className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </GlassCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="cf-glass border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="category-dialog">
          <DialogHeader><DialogTitle className="font-playfair text-xl">{editingId ? "Edit Category" : "New Category"}</DialogTitle>
            <DialogDescription className="text-white/50">Manage category details, imagery and SEO content.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-white/80">Name *</Label><Input data-testid="category-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 bg-black/30 border-white/15 text-white" /></div>
              <div><Label className="text-white/80">Slug (auto if blank)</Label><Input data-testid="category-slug-input" value={form.slug} onChange={(e) => set("slug", e.target.value)} className="mt-1 bg-black/30 border-white/15 text-white" /></div>
            </div>
            <div><Label className="text-white/80">Short description</Label><Input value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className="mt-1 bg-black/30 border-white/15 text-white" /></div>
            <div><Label className="text-white/80">Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1 bg-black/30 border-white/15 text-white" /></div>
            <div>
              <Label className="text-white/80">Category image</Label>
              <MediaUploader value={form.image ? [{ url: form.image, is_primary: true }] : []} onChange={(m) => set("image", m[0]?.url || "")} />
            </div>
            <div className="grid sm:grid-cols-3 gap-4 items-end">
              <div><Label className="text-white/80">Display order</Label><Input type="number" value={form.sort_order} onChange={(e) => set("sort_order", parseInt(e.target.value) || 0)} className="mt-1 bg-black/30 border-white/15 text-white" /></div>
              <div className="flex items-center gap-2"><Switch data-testid="category-active-switch" checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} /><Label className="text-white/80">Active</Label></div>
              <div className="flex items-center gap-2"><Switch data-testid="category-featured-switch" checked={form.is_featured} onCheckedChange={(v) => set("is_featured", v)} /><Label className="text-white/80">Featured</Label></div>
            </div>
            <div className="border-t border-white/10 pt-4">
              <SeoTab
                entityType="category"
                entity={{ category_id: editingId, name: form.name, description: form.description, short_description: form.short_description, image: form.image, seo: form.seo }}
                value={form.seo}
                onChange={(seo) => set("seo", seo)}
                slug={form.slug}
                onSlugChange={(v) => set("slug", v)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70">Cancel</Button>
            <Button data-testid="save-category-btn" onClick={save} disabled={saving} className="cf-gold-btn font-semibold gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
